import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isEmptyWidgetData, toggleWidgetTheme, updateSelectedSymbolInDropdown, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseCompanyStats {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private widgetResizeObserver: ResizeObserver | null = null;
    private widgetIndex: number = 0;

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(() => this.updateDisplay());
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        const symbols = this.fetchSymbols();
        return {
      headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        this.attachStyles();
        this.setupEventListeners();
        await this.setupChart(this.config.symbol!);
        if (widget) widget.classList.remove('loading');

        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const symbolOptions = this.fetchSymbols()
            .map(s => `<option value="${s.value}">${s.label}</option>`).join('');

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Company Stats</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow">
              <div class="item">
                <select class="symbol-dropdown" data-testing-id="symbol-dropdown">
                  ${symbolOptions}
                </select>
              </div>
            </div>

            <div class="chartDv">
                <div class="cryptoLiveCards cardsCarousel" id="keyMarticsContainer"> </div>
            </div>

          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
    }

    private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
        const symbolDropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        symbolDropdown?.addEventListener('change', e => {
            const value = (e.target as HTMLSelectElement).value;
            this.changeSymbol(value);
        });
        if (symbolDropdown && this.config.symbol) symbolDropdown.value = this.config.symbol;
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(symbol: string): Promise<void> {
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        try {
            const url = `${APIURL}/stocks/highlights/symbol=${symbol}`;
            console.log("Request URL:", url);
    
            const res = await fetch(url, {
                method: "GET",
                headers: {
                    'Content-Type': 'application/json'
                },
            });
    
            console.log("Response object:", res);
    
            if (!res.ok) {
                const text = await res.text();
                console.error("API Response Error:", text);
                throw new Error(`HTTP error! status: ${res.status}`);
            }
    
            const data = await res.json();
            console.log("API Data:", data);
    
            this.currentData = data;
            
            if (!this.currentData?.data || this.currentData.data.length === 0) {
                widgetBox?.removeAttribute('data-testing-id');
            }
            
            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
    
        } catch (err: any) {
            console.error("Fetch Failed:", err.message);
            console.error("Full Error:", err);
            widgetBox?.removeAttribute('data-testing-id');
        }
    }

    private updateDisplay(): void {
        if (!this.currentData) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.renderKeyMatrics(this.currentData.data);
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (symbol: any) => this.setupChart(symbol), (type: any, data: any) => this.emit(type, data));
    }

    private async renderKeyMatrics(dataArr: any[]): Promise<void> {
        const container = this.shadowRoot.querySelector('#keyMarticsContainer');
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        
        if (!container || !Array.isArray(dataArr) || dataArr.length === 0) {
            widgetBox?.removeAttribute('data-testing-id');
            if (container) container.innerHTML = getNoDataHTML();
            return;
        }

        await this.injectChartLibrary();
        container.innerHTML = '';

        dataArr.forEach((coin, index) => {
            const rawPrice = coin?.price ?? coin?.latestPrice;
            let price = "-";
            if (rawPrice !== undefined && rawPrice !== null && !isNaN(Number(rawPrice))) {
                price = Number(rawPrice).toFixed(4);
            }
            const pctChange = typeof coin?.pctChange === 'number' && !isNaN(coin.pctChange)
                ? coin.pctChange
                : (coin?.pricingData?.[0] ? ((coin.pricingData[0].close - coin.pricingData[0].open) / coin.pricingData[0].open) * 100 : 0);
            const isPositive = pctChange >= 0;
            const liveColorClass = isPositive ? 'positive' : 'negative';
            const liveColor = isPositive ? '#00ff91' : '#ff4d4d';
            const logoUrl = `https://media.traderverse.io/assets/crypto/${(coin.symbol || '').toUpperCase().replace('USD', '')}_CRYPTO.svg`;
            const dummyImg = "https://cdn-images.traderverse.io/crypto_dummy.svg";

            const priceChart = Array.isArray(coin?.priceChart) ? coin.priceChart : [];
            const chartYData = priceChart.length > 0
                ? priceChart.map((c: any) => typeof c.close === 'number' ? c.close : c.price)
                : [10, 50, 48, 30, 50, 20, 50, 30, 60, 55, 90, 128, 135, 132, 140];
            const chartXData = priceChart.length > 0 ? priceChart.map((_c: any, i: number) => i) : chartYData.map((_v: number, i: number) => i);

            const card = document.createElement('div');
            card.className = 'cryptoCard chartinCard';
            card.setAttribute('data-testing-id', `company-stats-card-${index}`);
            card.setAttribute('data-symbol', coin.symbol || '');
            card.style.cssText = ``;

            const latest = coin.price ?? coin.latestPrice;
            const formatLargeNumber = (n: number): string => {
                const abs = Math.abs(n);
                const sign = n < 0 ? '-' : '';
                if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(2)}t`;
                if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}b`;
                if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}m`;
                if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(2)}k`;
                const rounded = Math.round(n * 10000) / 10000;
                return `${sign}$${rounded.toString()}`;
            };
            const formattedPrice = (typeof latest === 'number' && !isNaN(latest)) ? formatLargeNumber(latest) : (price === "-" ? "-" : `$${price}`);

            card.innerHTML = `
                <div class="textDv">
                    <h2>
                        <figure><img src="${logoUrl}" onerror="this.onerror=null; this.src='${dummyImg}'" width="20" height="20" alt="${coin.symbol}" data-testing-id="company-stats-card-${index}-logo" /></figure>
                        <span data-testing-id="company-stats-card-${index}-symbol">${coin.symbol}</span>
                    </h2>
                    <div class="valueWrapper">
                        <h3 data-testing-id="company-stats-card-${index}-price">${formattedPrice}</h3>
                        <span class="${liveColorClass}" data-testing-id="company-stats-card-${index}-change">${(pctChange >= 0 ? '+' : '')}${pctChange.toFixed(2)}%</span>
                    </div>
                </div>
                <div class="inChart"><div id="chart-${index}" style="width:100%; height:40px; margin-top:5px;" data-testing-id="company-stats-card-${index}-chart"></div></div>
            `;

            container.appendChild(card);

            const chartEl = card.querySelector(`#chart-${index}`) as HTMLElement;
            const chart = (window as any).echarts.init(chartEl, null, { renderer: 'canvas' });

            chart.setOption({
                animation: true,
                grid: { left: 0, right: 0, top: 0, bottom: 0 },
                xAxis: { type: 'category', show: false, data: chartXData, boundaryGap: false },
                yAxis: { type: 'value', show: false, scale: true },
                tooltip: { show: false },
                series: [{
                    name: coin.companyName || coin.symbol,
                    type: 'line',
                    data: chartYData,
                    smooth: true,
                    symbol: 'none',
                    lineStyle: { width: 2, color: liveColor },
                    areaStyle: { opacity: 0.15, color: liveColor }
                }]
            });
        });
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    
    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M15.15 20.3C12.3125 20.3 10 17.9875 10 15.15C10 12.3125 12.3125 10 15.15 10C17.9875 10 20.3 12.3125 20.3 15.15C20.3 17.9875 18 20.3 15.15 20.3ZM15.15 11.8625C13.3375 11.8625 11.875 13.3375 11.875 15.1375C11.875 16.9375 13.35 18.4125 15.15 18.4125C16.95 18.4125 18.425 16.9375 18.425 15.1375C18.425 13.3375 16.9625 11.8625 15.15 11.8625Z" fill="currentColor"/> <path d="M15 28.4375C7.5875 28.4375 1.5625 22.4125 1.5625 15C1.5625 7.5875 7.5875 1.5625 15 1.5625C22.4125 1.5625 28.4375 7.5875 28.4375 15C28.4375 22.4125 22.4125 28.4375 15 28.4375ZM15 3.4375C8.625 3.4375 3.4375 8.625 3.4375 15C3.4375 21.375 8.625 26.5625 15 26.5625C21.375 26.5625 26.5625 21.375 26.5625 15C26.5625 8.625 21.375 3.4375 15 3.4375Z" fill="currentColor"/> <path d="M15.0625 22C14.5158 22 14.0625 21.5192 14.0625 20.9394V9.06061C14.0625 8.48081 14.5158 8 15.0625 8C15.6092 8 16.0625 8.48081 16.0625 9.06061V20.9394C16.0625 21.5192 15.6092 22 15.0625 22Z" fill="currentColor"/> </svg>`;
    }
}

declare global { interface Window { TraderverseCompanyStats: typeof TraderverseCompanyStats; } }
if (typeof window !== 'undefined') { window.TraderverseCompanyStats = TraderverseCompanyStats; }